// src/components/testimonials/TestimonialSubmissionModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, Star, Send, User, Building, Mail, MessageCircle, 
  CheckCircle, Loader2, AlertCircle, Heart
} from 'lucide-react';
import { profileService } from '../../../services/profileService';

export const TestimonialSubmissionModal = ({ 
  isOpen, 
  onClose, 
  profileId, 
  profileOwnerName
}) => {
  const [formData, setFormData] = useState({
    clientName: '',
    clientTitle: '',
    clientCompany: '',
    testimonial: '',
    rating: 5,
    projectTitle: '',
    serviceCategory: '',
    contactPermission: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.clientName.trim()) return 'Please enter your name';
    if (!formData.testimonial.trim()) return 'Please enter your testimonial';
    if (formData.testimonial.length < 20) return 'Testimonial must be at least 20 characters';
    if (formData.testimonial.length > 1000) return 'Testimonial must be less than 1000 characters';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Create testimonial section data using existing structure
      const testimonialSection = {
        type: 'testimonials',
        title: 'Testimonials',
        items: [{
          id: Date.now(),
          clientName: formData.clientName,
          clientTitle: formData.clientTitle,
          clientCompany: formData.clientCompany,
          testimonial: formData.testimonial,
          rating: formData.rating,
          date: new Date().toISOString().split('T')[0],
          projectTitle: formData.projectTitle,
          serviceCategory: formData.serviceCategory,
          isVerified: false,
          showOnWebsite: false, // Default to private
          contactPermission: formData.contactPermission,
          order: 0,
          submittedByPublic: true // Flag to identify public submissions
        }],
        showOnWebsite: false, // Private by default
        order: 999
      };

      // Check if testimonials section already exists
      const sections = await profileService.getProfileSections(profileId);
      const existingTestimonialSection = sections.find(s => s.type === 'testimonials');

      if (existingTestimonialSection) {
        // Add to existing testimonials section
        const updatedItems = [...(existingTestimonialSection.items || []), ...testimonialSection.items];
        await profileService.updateProfileSection(profileId, existingTestimonialSection.id, {
          ...existingTestimonialSection,
          items: updatedItems
        });
      } else {
        // Create new testimonials section
        await profileService.addProfileSection(profileId, testimonialSection);
      }

      setIsSubmitted(true);
      
      // Auto-close after 3 seconds on success
      setTimeout(() => {
        onClose();
        resetForm();
      }, 3000);
      
    } catch (err) {
      console.error('Error submitting testimonial:', err);
      setError('Failed to submit testimonial. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      clientName: '',
      clientTitle: '',
      clientCompany: '',
      testimonial: '',
      rating: 5,
      projectTitle: '',
      serviceCategory: '',
      contactPermission: false
    });
    setIsSubmitted(false);
    setError('');
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetForm, 300);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform overflow-y-auto">
        <div className="flex flex-col min-h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white sticky top-0 z-10">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Share Your Experience
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Tell others about working with {profileOwnerName}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 p-6">
            {isSubmitted ? (
              // Success State
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Thank You!
                </h3>
                <p className="text-gray-600 mb-6">
                  Your testimonial has been submitted successfully. 
                  {profileOwnerName} will review it and decide whether to make it public.
                </p>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>What's Next?</strong><br />
                    • Your testimonial is saved privately<br />
                    • The profile owner can choose to make it public<br />
                    • Public testimonials help build trust and credibility
                  </p>
                </div>
              </div>
            ) : (
              // Form State
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    About You
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      value={formData.clientName}
                      onChange={(e) => handleInputChange('clientName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="John Smith"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Your Title/Position
                      </label>
                      <input
                        type="text"
                        value={formData.clientTitle}
                        onChange={(e) => handleInputChange('clientTitle', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="CEO, Manager, etc."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company
                      </label>
                      <input
                        type="text"
                        value={formData.clientCompany}
                        onChange={(e) => handleInputChange('clientCompany', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Company Name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Project Title (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.projectTitle}
                        onChange={(e) => handleInputChange('projectTitle', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Website redesign, etc."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Category
                      </label>
                      <select
                        value={formData.serviceCategory}
                        onChange={(e) => handleInputChange('serviceCategory', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select category</option>
                        <option value="consulting">Consulting</option>
                        <option value="development">Development</option>
                        <option value="design">Design</option>
                        <option value="marketing">Marketing</option>
                        <option value="support">Support</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Testimonial */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Your Testimonial
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rating
                    </label>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleInputChange('rating', star)}
                          className="p-1 hover:scale-110 transition-transform"
                        >
                          <Star 
                            className={`h-6 w-6 ${
                              star <= formData.rating 
                                ? 'text-yellow-400 fill-current' 
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-gray-600">
                        {formData.rating}/5
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Experience *
                    </label>
                    <textarea
                      value={formData.testimonial}
                      onChange={(e) => handleInputChange('testimonial', e.target.value)}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      placeholder="Share your experience working with them. What made it exceptional? How did they help you achieve your goals?"
                      required
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {formData.testimonial.length}/1000 characters 
                      {formData.testimonial.length < 20 && formData.testimonial.length > 0 && (
                        <span className="text-red-500 ml-1">
                          (minimum 20 characters)
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Privacy */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.contactPermission}
                      onChange={(e) => handleInputChange('contactPermission', e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">
                        Allow potential clients to contact me
                      </span>
                      <p className="text-gray-600 mt-1">
                        {profileOwnerName} may share your contact details with potential clients who want to verify your testimonial.
                      </p>
                    </div>
                  </label>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Submit Testimonial
                    </>
                  )}
                </button>

                {/* Privacy Notice */}
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Privacy Note:</strong> Your testimonial will be saved privately and only made public if the profile owner chooses to do so. Your contact information will not be shared publicly.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};